import { memo, useCallback } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { v4 as uuidv4 } from "uuid";

import { Box } from "@mui/material";

import ScrollDownButton from "@/components/atoms/buttons/scrollDownButton";

import { useLayoutMaxWidth } from "@/hooks/useLayoutMaxWidth";

import { IAttachment } from "@/state/chat";
import { inputHistoryState } from "@/state/userInputHistory";

import Input from "./input";
import { FileSpec, IStep } from "@/client/types";
import { useAuth } from "@/client/api";
import { useChatInteract } from "@/client/useChatInteractV2";
import { currentFileRefIdState, currentThreadIdState } from "@/client/state";

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (payload: File[]) => void;
  onFileUploadError: (error: string) => void;
  setAutoScroll: (autoScroll: boolean) => void;
  autoScroll?: boolean;
  page: "session" | "report";
  companyId?: string | number | undefined;
}

const InputBox = memo(
  ({
    fileSpec,
    onFileUpload,
    onFileUploadError,
    setAutoScroll,
    autoScroll,
    page,
    companyId,
  }: Props) => {
    const layoutMaxWidth = useLayoutMaxWidth();
    const currentThreadId = useRecoilValue(currentThreadIdState);
    const setInputHistory = useSetRecoilState(inputHistoryState);
    const setCurrentFileRefId = useSetRecoilState(currentFileRefIdState);

    const { user } = useAuth();
    const { sendMessage, replyMessage } = useChatInteract();
    // const tokenCount = useRecoilValue(tokenCountState);

    const onSubmit = useCallback(
      async (msg: string, attachments?: IAttachment[]) => {
        const message: IStep = {
          thread_id: currentThreadId || "",
          id: uuidv4(),
          name: user?.identifier || "User",
          role: "user",
          content: msg,
          timestamp: new Date().toISOString(),
        };

        setInputHistory((old) => {
          const MAX_SIZE = 50;
          const inputs = [...(old.inputs || [])];
          inputs.push({
            content: msg,
            createdAt: new Date().getTime(),
          });

          return {
            ...old,
            inputs:
              inputs.length > MAX_SIZE
                ? inputs.slice(inputs.length - MAX_SIZE)
                : inputs,
          };
        });

        const fileReferences = attachments
          ?.filter((a) => !!a.serverId)
          .map((a) => ({ id: a.serverId! }));

        let fileRefId = null;

        if (fileReferences && fileReferences.length > 0) {
          fileRefId = fileReferences[0].id as unknown as number;
          setCurrentFileRefId(fileRefId);
        }

        setAutoScroll(true);

        if (page === "session") {
          sendMessage(message, companyId, fileRefId);
        } else if (page === "report") {
          // sendReport(message, companyId, fileRefId);
        }
      },
      [user, currentThreadId, sendMessage, companyId]
    );

    const onReply = useCallback(
      async (msg: string) => {
        const message: IStep = {
          thread_id: "",
          id: uuidv4(),
          name: user?.identifier || "User",
          role: "user",
          content: msg,
          timestamp: new Date().toISOString(),
        };

        replyMessage(message);
        setAutoScroll(true);
      },
      [user, replyMessage]
    );

    return (
      <Box
        display="flex"
        position="relative"
        flexDirection="column"
        gap={1}
        pb={1}
        px={2}
        sx={{
          boxSizing: "border-box",
          width: "100%",
          maxWidth: layoutMaxWidth,
          m: "auto",
          justifyContent: "center",
        }}
      >
        {!autoScroll ? (
          <ScrollDownButton onClick={() => setAutoScroll(true)} />
        ) : null}
        <Box>
          <Input
            fileSpec={fileSpec}
            onFileUpload={onFileUpload}
            onFileUploadError={onFileUploadError}
            onSubmit={onSubmit}
            onReply={onReply}
            page={page}
          />
          {/* {tokenCount > 0 && ( */}
          {/* <Stack flexDirection="row" alignItems="center">
          <Typography
            sx={{ ml: 'auto' }}
            color="text.secondary"
            variant="caption"
          >
            Token usage: {tokenCount}
          </Typography>
        </Stack> */}
          {/* )} */}
        </Box>
      </Box>
    );
  }
);

export default InputBox;
